// frontend/src/features/meetings/components/ScheduleGroupMeetingModal.js
import React, { useState, useRef, useEffect, useMemo } from 'react';
import axios from 'axios';
import meetingsApi from '../api/meetingsApi';
import calendarApi from '../api/calendarApi';

export const DEFAULT_WORKING_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
export const DAYS_OF_WEEK_FULL = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export const extractEmailString = (rawEmail) => {
  if (!rawEmail) return '';
  if (typeof rawEmail === 'string') return rawEmail.trim();
  if (Array.isArray(rawEmail)) {
    for (const item of rawEmail) {
      const s = extractEmailString(item);
      if (s) return s;
    }
    return '';
  }
  if (typeof rawEmail === 'object') {
    if (rawEmail.email) return extractEmailString(rawEmail.email);
    if (rawEmail.value) return extractEmailString(rawEmail.value);
    if (rawEmail.address) return extractEmailString(rawEmail.address);
    if (rawEmail.contact_email) return extractEmailString(rawEmail.contact_email);
    return '';
  }
  return String(rawEmail).trim();
};

export const safeStringVal = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    for (const item of value) {
      const s = safeStringVal(item, '');
      if (s) return s;
    }
    return fallback;
  }
  if (typeof value === 'object') {
    for (const k of ['name', 'value', 'title', 'label', 'stage', 'company']) {
      if (value[k]) {
        const s = safeStringVal(value[k], '');
        if (s) return s;
      }
    }
    return fallback;
  }
  return String(value).trim();
};

export const isActiveAndApprovedCampaign = (cmp) => {
  if (!cmp) return false;
  const status = String(cmp.status || '')
    .trim()
    .toUpperCase();
  const isApproved =
    Boolean(cmp.approvedBy) ||
    cmp.governance?.allGatesApproved === true ||
    cmp.governance?.isCertified === true ||
    cmp.isApproved === true;

  return (
    (status === 'ACTIVE' || status === 'RUNNING' || status === 'APPROVED') &&
    isApproved &&
    status !== 'PENDING_APPROVAL' &&
    status !== 'CHANGES_REQUESTED' &&
    status !== 'DRAFT' &&
    status !== 'PAUSED'
  );
};

export const formatTimeDisplay = (timeObjOrStr) => {
  if (!timeObjOrStr) return '';
  let dt;
  if (timeObjOrStr instanceof Date) {
    dt = timeObjOrStr;
  } else if (typeof timeObjOrStr === 'string' && timeObjOrStr.includes(':')) {
    const parts = timeObjOrStr.split('T');
    const timePart = parts.length > 1 ? parts[1] : parts[0];
    const [h, m] = timePart.split(':').map((v) => parseInt(v, 10) || 0);
    dt = new Date(2000, 0, 1, h, m, 0);
  } else {
    dt = new Date(timeObjOrStr);
  }
  if (isNaN(dt.getTime())) return String(timeObjOrStr);
  return dt.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

export const parseTimeToMinutes = (tStr = '', defaultMin = 540) => {
  if (!tStr) return defaultMin;
  const clean = String(tStr).trim();
  if (clean.includes('AM') || clean.includes('PM')) {
    const parts = clean.split(/\s+/);
    const [h, m] = (parts[0] || '09:00').split(':').map(Number);
    let hours = (h || 0) % 12;
    if (parts[1]?.toUpperCase() === 'PM') hours += 12;
    return hours * 60 + (m || 0);
  }
  const [h, m] = clean.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

export const generateHostTimeSlots = (startStr = '09:00', endStr = '18:00', durMins = 45) => {
  const startMin = parseTimeToMinutes(startStr, 9 * 60);
  const endMin = parseTimeToMinutes(endStr, 18 * 60);
  const dur = Number(durMins) || 45;
  const step = 30;
  const slots = [];
  for (let m = startMin; m + dur <= endMin; m += step) {
    const hh = String(Math.floor(m / 60)).padStart(2, '0');
    const mm = String(m % 60).padStart(2, '0');
    slots.push(`${hh}:${mm}`);
  }
  return slots.length > 0
    ? slots
    : [
        '09:00',
        '09:30',
        '10:00',
        '10:30',
        '11:00',
        '11:30',
        '13:30',
        '14:00',
        '14:30',
        '15:00',
        '15:30',
        '16:00',
        '16:30',
        '17:00',
      ];
};

const DURATION_OPTIONS = [15, 30, 45, 60, 90];

const getAvatarColor = (name = '') => {
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#6366f1'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

export const getRoleTierLevel = (role = '') => {
  if (!role) return 1;
  const clean = String(role)
    .trim()
    .toLowerCase()
    .replace(/^org_/, '')
    .replace(/[\s-]+/g, '_');
  if (['owner', 'workspace_owner', 'superadmin'].includes(clean) || clean.includes('owner'))
    return 4;
  if (['admin', 'org_admin', 'platform_admin'].includes(clean) || clean.includes('admin')) return 3;
  if (
    ['manager', 'sales_manager', 'sales_lead', 'director', 'head'].includes(clean) ||
    clean.includes('manager') ||
    clean.includes('lead') ||
    clean.includes('director') ||
    clean.includes('head')
  )
    return 2;
  return 1;
};

export const formatRoleLabel = (role = '') => {
  if (!role) return 'Member';
  const clean = String(role)
    .trim()
    .toLowerCase()
    .replace(/^org_/, '')
    .replace(/[\s-]+/g, '_');
  if (clean === 'owner') return 'Owner';
  if (clean === 'admin') return 'Admin';
  if (clean.includes('manager')) return 'Sales Manager';
  if (clean.includes('lead')) return 'Sales Lead';
  if (clean.includes('researcher')) return 'Researcher';
  if (clean.includes('viewer')) return 'Viewer';
  return 'Sales Member';
};

const getInitials = (name = '') => {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const isSlotInPast = (slotTime, dateStr) => {
  if (!slotTime || !dateStr) return false;
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    const [h, min] = slotTime.split(':').map(Number);
    const slotDate = new Date(y, m - 1, d, h, min, 0);
    const now = new Date();
    return slotDate.getTime() <= now.getTime();
  } catch {
    return false;
  }
};

const formatSlotTimeStr = (dateObj) => {
  if (!dateObj || isNaN(dateObj.getTime())) return '';
  let hours = dateObj.getHours();
  const minutes = dateObj.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const minsStr = minutes < 10 ? '0' + minutes : minutes;
  return `${hours}:${minsStr} ${ampm}`;
};

export const parseLeadItem = (rawLead, fallbackIndex = 0) => {
  if (!rawLead || typeof rawLead !== 'object') return null;
  const id = String(
    rawLead.id ||
      rawLead.lead_id ||
      rawLead.leadId ||
      rawLead.shared_item_id ||
      rawLead.crmId ||
      rawLead.external_id ||
      `lead-${fallbackIndex}`
  ).trim();

  const email = extractEmailString(
    rawLead.email ||
      rawLead.contact_email ||
      rawLead.contactEmail ||
      rawLead.crm_meta?.email ||
      rawLead.crm_meta?.contact_email
  );

  const name = safeStringVal(
    rawLead.name ||
      rawLead.contact_name ||
      rawLead.contactName ||
      rawLead.full_name ||
      rawLead.fullName ||
      rawLead.crm_meta?.name,
    email ? email.split('@')[0] : 'Lead Contact'
  );

  const company = safeStringVal(
    rawLead.company ||
      rawLead.company_name ||
      rawLead.companyName ||
      rawLead.organization ||
      rawLead.account_name ||
      rawLead.crm_meta?.company ||
      rawLead.crm_meta?.companyName,
    'Prospect Org'
  );

  const jobTitle = safeStringVal(
    rawLead.jobTitle ||
      rawLead.job_title ||
      rawLead.title ||
      rawLead.role ||
      rawLead.position ||
      rawLead.crm_meta?.jobTitle ||
      rawLead.crm_meta?.job_title,
    'Decision Maker'
  );

  const stage = safeStringVal(
    rawLead.stage || rawLead.crm_meta?.stage || rawLead.pipeline_stage,
    'New'
  );

  const leadScore =
    Number(
      rawLead.leadScore || rawLead.lead_score || rawLead.score || rawLead.crm_meta?.lead_score
    ) || 75;

  const dealEstimate = safeStringVal(
    rawLead.dealEstimate ||
      rawLead.deal_estimate ||
      rawLead.dealValue ||
      rawLead.deal_value ||
      rawLead.value ||
      rawLead.crm_meta?.dealValue,
    '$5,000'
  );

  const persona = safeStringVal(
    rawLead.persona || rawLead.targetPersona || rawLead.target_persona,
    'Decision Maker'
  );

  return {
    id,
    crmId: rawLead.crmId || rawLead.lead_id || rawLead.id || id,
    name,
    email,
    company,
    jobTitle,
    stage,
    leadScore,
    dealEstimate,
    persona,
  };
};

export default function ScheduleGroupMeetingModal({
  hosts = [],
  meetingTypes = [],
  campaigns: propCampaigns = null,
  onClose,
  onSchedule,
  defaultHostId,
  defaultDate,
  workspaceId,
  currentUser,
  currentUserRole,
  isOwner = false,
  existingMeetings = [],
  availabilitySettings = null,
}) {
  const hostRef = useRef(null);
  const typeRef = useRef(null);
  const timeDropdownRef = useRef(null);
  const campaignRef = useRef(null);

  const [isHostOpen, setIsHostOpen] = useState(false);
  const [isTypeOpen, setIsTypeOpen] = useState(false);
  const [isTimeOpen, setIsTimeOpen] = useState(false);
  const [isCampaignOpen, setIsCampaignOpen] = useState(false);

  // Available campaigns (strictly active & approved only from database/props)
  const [rawCampaigns, setRawCampaigns] = useState(() => {
    if (Array.isArray(propCampaigns) && propCampaigns.length > 0) {
      return propCampaigns;
    }
    try {
      const saved = localStorage.getItem('saved_campaigns');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
    } catch (_) {}
    return [];
  });

  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);

  // Load real workspace campaigns from API
  useEffect(() => {
    let isMounted = true;
    if (Array.isArray(propCampaigns) && propCampaigns.length > 0) {
      setRawCampaigns(propCampaigns);
      return;
    }

    setIsLoadingCampaigns(true);
    const fetchCampaigns = async () => {
      let fetched = [];
      try {
        fetched = await meetingsApi.getWorkspaceCampaigns(workspaceId);
      } catch (err) {
        console.warn('Failed to fetch workspace campaigns:', err);
      }

      if (!Array.isArray(fetched) || fetched.length === 0) {
        try {
          const saved = localStorage.getItem('saved_campaigns');
          if (saved) {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed) && parsed.length > 0) {
              fetched = parsed;
            }
          }
        } catch (_) {}
      }

      if (isMounted) {
        setRawCampaigns(Array.isArray(fetched) ? fetched : []);
        setIsLoadingCampaigns(false);
      }
    };

    fetchCampaigns();
    return () => {
      isMounted = false;
    };
  }, [workspaceId, propCampaigns]);

  const campaignsList = useMemo(() => {
    return (rawCampaigns || []).filter(isActiveAndApprovedCampaign);
  }, [rawCampaigns]);

  const [selectedCampaignId, setSelectedCampaignId] = useState('');

  // Auto-select first active & approved campaign if current is invalid
  useEffect(() => {
    if (campaignsList.length > 0) {
      if (!selectedCampaignId || !campaignsList.some((c) => c.id === selectedCampaignId)) {
        setSelectedCampaignId(campaignsList[0].id);
      }
    } else {
      setSelectedCampaignId('');
    }
  }, [campaignsList, selectedCampaignId]);

  const selectedCampaign = useMemo(() => {
    if (!campaignsList || campaignsList.length === 0) return null;
    return campaignsList.find((c) => c.id === selectedCampaignId) || campaignsList[0] || null;
  }, [campaignsList, selectedCampaignId]);

  // Campaign leads state
  const [availableLeads, setAvailableLeads] = useState([]);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [leadFilterQuery, setLeadFilterQuery] = useState('');
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);

  // Load leads strictly for the selected campaign
  useEffect(() => {
    let isMounted = true;
    setIsLoadingLeads(true);

    const loadLeads = async () => {
      if (!selectedCampaign) {
        setAvailableLeads([]);
        setSelectedLeadIds([]);
        setIsLoadingLeads(false);
        return;
      }

      let pipeLeads = [];
      try {
        pipeLeads = await meetingsApi.getPipelineLeads(workspaceId);
      } catch (_) {}

      if (!isMounted) return;

      const campaignId = String(selectedCampaign.id || '').trim();
      const campaignName = String(selectedCampaign.name || '')
        .trim()
        .toLowerCase();

      // 1. Direct campaign attached leads
      const rawDirectList = [];
      const addRaw = (l) => {
        if (l && typeof l === 'object') rawDirectList.push(l);
      };

      if (Array.isArray(selectedCampaign.importedLeads))
        selectedCampaign.importedLeads.forEach(addRaw);
      if (Array.isArray(selectedCampaign.imported_leads))
        selectedCampaign.imported_leads.forEach(addRaw);
      if (Array.isArray(selectedCampaign.targetLeads)) selectedCampaign.targetLeads.forEach(addRaw);
      if (Array.isArray(selectedCampaign.target_leads))
        selectedCampaign.target_leads.forEach(addRaw);
      if (Array.isArray(selectedCampaign.leads)) selectedCampaign.leads.forEach(addRaw);
      if (Array.isArray(selectedCampaign.prospects)) selectedCampaign.prospects.forEach(addRaw);
      if (Array.isArray(selectedCampaign.selectedLeads))
        selectedCampaign.selectedLeads.forEach(addRaw);

      const strat =
        typeof selectedCampaign.strategy === 'string'
          ? (() => {
              try {
                return JSON.parse(selectedCampaign.strategy);
              } catch (_) {
                return {};
              }
            })()
          : selectedCampaign.strategy || {};
      if (Array.isArray(strat.importedLeads)) strat.importedLeads.forEach(addRaw);
      if (Array.isArray(strat.targetLeads)) strat.targetLeads.forEach(addRaw);

      const parsedDirect = [];
      const seenDirectKeys = new Set();
      rawDirectList.forEach((raw, idx) => {
        const parsed = parseLeadItem(raw, idx);
        if (parsed) {
          const key = `${parsed.id}-${parsed.email}`.toLowerCase();
          if (!seenDirectKeys.has(key)) {
            seenDirectKeys.add(key);
            parsedDirect.push(parsed);
          }
        }
      });

      // 2. Filter CRM pipeline leads that specifically belong to this campaign
      const matchingPipeLeads = [];
      if (Array.isArray(pipeLeads) && pipeLeads.length > 0) {
        pipeLeads.forEach((pl, idx) => {
          const plCampaignId = String(
            pl.campaign_id ||
              pl.campaignId ||
              pl.crm_meta?.campaignId ||
              pl.crm_meta?.campaign_id ||
              ''
          ).trim();
          const plCampaignName = String(
            pl.campaign_name ||
              pl.campaignName ||
              pl.crm_meta?.campaignName ||
              pl.crm_meta?.campaign_name ||
              ''
          )
            .trim()
            .toLowerCase();

          const isMatch =
            (plCampaignId && plCampaignId === campaignId) ||
            (plCampaignName &&
              (plCampaignName === campaignName ||
                campaignName.includes(plCampaignName) ||
                plCampaignName.includes(campaignName)));

          if (isMatch) {
            const parsed = parseLeadItem(pl, `pipe-${idx}`);
            if (parsed) {
              matchingPipeLeads.push(parsed);
            }
          }
        });
      }

      // Combine direct campaign leads + matching pipeline leads
      let finalLeads = [...parsedDirect];
      matchingPipeLeads.forEach((ml) => {
        if (
          !finalLeads.some(
            (fl) =>
              (fl.id && String(fl.id) === String(ml.id)) ||
              (fl.crmId && String(fl.crmId) === String(ml.crmId)) ||
              (fl.email && ml.email && fl.email.toLowerCase() === ml.email.toLowerCase())
          )
        ) {
          finalLeads.push(ml);
        }
      });

      // Strict real leads only: if campaign has no leads attached, finalLeads is empty []
      setAvailableLeads(finalLeads);
      // Do not default-select leads; user selects them manually (at least 1 lead required)
      setSelectedLeadIds([]);
      setIsLoadingLeads(false);
    };

    loadLeads();
    return () => {
      isMounted = false;
    };
  }, [selectedCampaign, workspaceId]);

  // Filtered leads
  const filteredAvailableLeads = useMemo(() => {
    if (!leadFilterQuery.trim()) return availableLeads;
    const q = leadFilterQuery.toLowerCase();
    return availableLeads.filter(
      (l) =>
        (l.name && l.name.toLowerCase().includes(q)) ||
        (l.company && l.company.toLowerCase().includes(q)) ||
        (l.email && l.email.toLowerCase().includes(q)) ||
        (l.jobTitle && l.jobTitle.toLowerCase().includes(q)) ||
        (l.stage && l.stage.toLowerCase().includes(q))
    );
  }, [availableLeads, leadFilterQuery]);

  const selectedLeadsObjects = useMemo(() => {
    return availableLeads.filter((l) => selectedLeadIds.includes(l.id));
  }, [availableLeads, selectedLeadIds]);

  const isCalendarConnected = useMemo(() => {
    if (!availabilitySettings || !Array.isArray(availabilitySettings.connectedCalendars)) {
      return true;
    }
    return availabilitySettings.connectedCalendars.some(
      (c) =>
        (c.provider === 'google' || c.provider === 'microsoft' || c.provider === 'outlook') &&
        c.status === 'connected'
    );
  }, [availabilitySettings]);

  const isGoogleCalendarConnected = isCalendarConnected;

  const effectiveCurrentUser = useMemo(() => {
    if (
      currentUser &&
      (currentUser.email || currentUser.id || currentUser.username || currentUser.name)
    ) {
      return currentUser;
    }
    try {
      const storedTokens =
        localStorage.getItem('keycloak_tokens') || sessionStorage.getItem('keycloak_tokens');
      if (storedTokens) {
        const parsed = JSON.parse(storedTokens);
        if (parsed?.access_token) {
          const base64Url = parsed.access_token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const tokenData = JSON.parse(atob(base64));
          if (tokenData) {
            return {
              id: tokenData.sub,
              email: tokenData.email,
              username: tokenData.preferred_username,
              name:
                tokenData.name ||
                [tokenData.given_name, tokenData.family_name].filter(Boolean).join(' ') ||
                tokenData.preferred_username,
            };
          }
        }
      }
    } catch (_) {}
    return currentUser || null;
  }, [currentUser]);

  const currentRoleLevel = useMemo(() => {
    if (isOwner) return 4;
    const currentMember = (hosts || []).find(
      (h) =>
        (effectiveCurrentUser?.id &&
          (String(h.id) === String(effectiveCurrentUser.id) ||
            String(h.userId) === String(effectiveCurrentUser.id))) ||
        (effectiveCurrentUser?.email &&
          h.email &&
          h.email.toLowerCase() === effectiveCurrentUser.email.toLowerCase()) ||
        (effectiveCurrentUser?.username &&
          h.username &&
          h.username.toLowerCase() === effectiveCurrentUser.username.toLowerCase()) ||
        (effectiveCurrentUser?.name &&
          h.name &&
          h.name.toLowerCase() === effectiveCurrentUser.name.toLowerCase())
    );
    const r =
      currentMember?.rawRole ||
      currentMember?.role ||
      currentUserRole ||
      effectiveCurrentUser?.workspace_role ||
      effectiveCurrentUser?.role;
    if (!r) return 4;
    return getRoleTierLevel(r);
  }, [isOwner, hosts, effectiveCurrentUser, currentUserRole]);

  const assignableHosts = useMemo(() => {
    if (!Array.isArray(hosts)) return [];
    return hosts.filter((h) => {
      const isCurrentUser =
        (effectiveCurrentUser?.id &&
          (String(h.id) === String(effectiveCurrentUser.id) ||
            String(h.userId) === String(effectiveCurrentUser.id))) ||
        (effectiveCurrentUser?.email &&
          h.email &&
          h.email.toLowerCase() === effectiveCurrentUser.email.toLowerCase()) ||
        (effectiveCurrentUser?.username &&
          h.username &&
          h.username.toLowerCase() === effectiveCurrentUser.username.toLowerCase()) ||
        (effectiveCurrentUser?.name &&
          h.name &&
          h.name.toLowerCase() === effectiveCurrentUser.name.toLowerCase());

      if (isCurrentUser) return false;
      const hostRoleLevel = getRoleTierLevel(h?.rawRole || h?.role);
      return hostRoleLevel < currentRoleLevel;
    });
  }, [hosts, currentRoleLevel, effectiveCurrentUser]);

  const [hostId, setHostId] = useState(defaultHostId || '');
  const [meetingTypeId, setMeetingTypeId] = useState(
    meetingTypes.find((t) => t.slug === 'product-demo' || t.title?.includes('Demo'))?.id ||
      meetingTypes[0]?.id ||
      'mt-group'
  );

  const [meetingTopic, setMeetingTopic] = useState(
    selectedCampaign?.name ? `${selectedCampaign.name} Cohort Session` : 'Executive Strategy & Demo'
  );
  const [hasUserEditedTopic, setHasUserEditedTopic] = useState(false);

  // Update default topic when campaign changes if user has not edited
  useEffect(() => {
    if (!hasUserEditedTopic && selectedCampaign?.name) {
      setMeetingTopic(`${selectedCampaign.name} Cohort Session`);
    }
  }, [selectedCampaign, hasUserEditedTopic]);

  const [durationMinutes, setDurationMinutes] = useState(30);
  const [guestNotes, setGuestNotes] = useState('');

  // Working days & hours from availability settings
  const hostWorkingDays = useMemo(() => {
    if (
      Array.isArray(availabilitySettings?.workingDays) &&
      availabilitySettings.workingDays.length > 0
    ) {
      return availabilitySettings.workingDays;
    }
    return DEFAULT_WORKING_DAYS;
  }, [availabilitySettings]);

  const hostWorkingHoursStart = availabilitySettings?.workingHoursStart || '09:00';
  const hostWorkingHoursEnd = availabilitySettings?.workingHoursEnd || '18:00';
  const bufferBeforeMinutes = Number(
    availabilitySettings?.bufferBeforeMinutes ?? (availabilitySettings ? 15 : 0)
  );
  const bufferAfterMinutes = Number(
    availabilitySettings?.bufferAfterMinutes ?? (availabilitySettings ? 15 : 0)
  );

  const dynamicTimeSlots = useMemo(() => {
    return generateHostTimeSlots(hostWorkingHoursStart, hostWorkingHoursEnd, durationMinutes);
  }, [hostWorkingHoursStart, hostWorkingHoursEnd, durationMinutes]);

  const isWorkingDay = (dateStr) => {
    if (!dateStr) return true;
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    const dayName = DAYS_OF_WEEK_FULL[dt.getDay()];
    return hostWorkingDays.some(
      (wd) =>
        wd.toLowerCase() === dayName.toLowerCase() ||
        wd.toLowerCase().startsWith(dayName.slice(0, 3).toLowerCase())
    );
  };

  const [date, setDate] = useState(() => {
    if (defaultDate) {
      if (typeof defaultDate === 'string' && defaultDate.includes('T')) {
        return defaultDate.split('T')[0];
      }
      if (defaultDate instanceof Date && !isNaN(defaultDate.getTime())) {
        const y = defaultDate.getFullYear();
        const m = String(defaultDate.getMonth() + 1).padStart(2, '0');
        const d = String(defaultDate.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      }
      return String(defaultDate);
    }
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    const todayFormatted = `${y}-${m}-${d}`;

    const initialSlots = generateHostTimeSlots('09:00', '18:00', 30);
    const initialWorkingDays =
      Array.isArray(availabilitySettings?.workingDays) &&
      availabilitySettings.workingDays.length > 0
        ? availabilitySettings.workingDays
        : DEFAULT_WORKING_DAYS;

    const checkIsWorking = (dStr) => {
      const [yr, mo, dy] = dStr.split('-').map(Number);
      const dt = new Date(yr, mo - 1, dy);
      const dayName = DAYS_OF_WEEK_FULL[dt.getDay()];
      return initialWorkingDays.some(
        (wd) =>
          wd.toLowerCase() === dayName.toLowerCase() ||
          wd.toLowerCase().startsWith(dayName.slice(0, 3).toLowerCase())
      );
    };

    if (
      checkIsWorking(todayFormatted) &&
      initialSlots.some((s) => !isSlotInPast(s, todayFormatted))
    ) {
      return todayFormatted;
    }

    for (let offset = 1; offset <= 14; offset++) {
      const nextDt = new Date(now);
      nextDt.setDate(now.getDate() + offset);
      const ny = nextDt.getFullYear();
      const nm = String(nextDt.getMonth() + 1).padStart(2, '0');
      const nd = String(nextDt.getDate()).padStart(2, '0');
      const nextDateStr = `${ny}-${nm}-${nd}`;
      if (checkIsWorking(nextDateStr)) {
        return nextDateStr;
      }
    }

    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const ty = tomorrow.getFullYear();
    const tm = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const td = String(tomorrow.getDate()).padStart(2, '0');
    return `${ty}-${tm}-${td}`;
  });

  const [timeSlot, setTimeSlot] = useState(() => {
    const initialSlots = generateHostTimeSlots('09:00', '18:00', 45);
    const firstFuture = initialSlots.find((s) => !isSlotInPast(s, date));
    return firstFuture || initialSlots[0] || '10:00';
  });

  const connectedCalendarProvider = useMemo(() => {
    const cal = availabilitySettings?.connectedCalendars?.find(
      (c) =>
        (c.provider === 'google' || c.provider === 'microsoft' || c.provider === 'outlook') &&
        c.status === 'connected'
    );
    if (cal?.provider === 'microsoft' || cal?.provider === 'outlook') return 'microsoft';
    if (cal?.provider === 'google') return 'google';
    return null;
  }, [availabilitySettings]);

  const [conferencingProvider, setConferencingProvider] = useState(() => {
    const cal = availabilitySettings?.connectedCalendars?.find(
      (c) =>
        (c.provider === 'google' || c.provider === 'microsoft' || c.provider === 'outlook') &&
        c.status === 'connected'
    );
    if (cal?.provider === 'microsoft' || cal?.provider === 'outlook') {
      return 'teams';
    }
    return 'google_meet';
  });
  const [platformNotice, setPlatformNotice] = useState(null);

  useEffect(() => {
    if (connectedCalendarProvider === 'microsoft') {
      setConferencingProvider('teams');
    } else if (connectedCalendarProvider === 'google') {
      setConferencingProvider('google_meet');
    }
  }, [connectedCalendarProvider]);
  const [externalBusySlots, setExternalBusySlots] = useState([]);
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);

  useEffect(() => {
    if (isSlotInPast(timeSlot, date) || !dynamicTimeSlots.includes(timeSlot)) {
      const firstFuture = dynamicTimeSlots.find((s) => !isSlotInPast(s, date));
      if (firstFuture) {
        setTimeSlot(firstFuture);
      } else if (dynamicTimeSlots.length > 0) {
        setTimeSlot(dynamicTimeSlots[0]);
      }
    }
  }, [date, timeSlot, dynamicTimeSlots]);

  // Selected Host resolution
  const selectedHost = useMemo(() => {
    if (!hostId || hostId === 'unassigned') {
      return { id: '', name: 'Unassigned', email: '', role: '', isUnassigned: true };
    }
    const found =
      assignableHosts.find((h) => h.id === hostId || h.userId === hostId) ||
      hosts.find((h) => h.id === hostId || h.userId === hostId);
    if (found) return found;
    return { id: '', name: 'Unassigned', email: '', role: '', isUnassigned: true };
  }, [assignableHosts, hosts, hostId]);

  const effectiveTargetPerson = useMemo(() => {
    const isUnassigned = !hostId || hostId === 'unassigned';
    if (isUnassigned) {
      return {
        id:
          effectiveCurrentUser?.id ||
          effectiveCurrentUser?.userId ||
          effectiveCurrentUser?.sub ||
          '',
        userId:
          effectiveCurrentUser?.userId ||
          effectiveCurrentUser?.id ||
          effectiveCurrentUser?.sub ||
          '',
        name:
          effectiveCurrentUser?.name ||
          effectiveCurrentUser?.fullName ||
          effectiveCurrentUser?.displayName ||
          effectiveCurrentUser?.username ||
          'Current Account',
        email: effectiveCurrentUser?.email || '',
        isUnassigned: true,
        isSelf: true,
      };
    }
    const found =
      assignableHosts.find((h) => h.id === hostId || h.userId === hostId) ||
      hosts.find((h) => h.id === hostId || h.userId === hostId);
    if (found) {
      return {
        ...found,
        id: found.id || found.userId || hostId,
        userId: found.userId || found.id || hostId,
        name: found.name || found.username || 'Host',
        email: found.email || '',
        isUnassigned: false,
        isSelf: false,
      };
    }
    return {
      id: hostId,
      userId: hostId,
      name: 'Host',
      email: '',
      isUnassigned: false,
      isSelf: false,
    };
  }, [hostId, assignableHosts, hosts, effectiveCurrentUser]);

  const selectedType = useMemo(() => {
    return (
      meetingTypes.find((t) => t.id === meetingTypeId) ||
      meetingTypes[0] || {
        id: 'mt-group',
        title: 'Group Session',
        color: '#3b82f6',
      }
    );
  }, [meetingTypes, meetingTypeId]);

  const formattedDisplayDate = useMemo(() => {
    if (!date) return '';
    try {
      const parts = date.split('-');
      if (parts.length === 3) {
        const y = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10) - 1;
        const d = parseInt(parts[2], 10);
        const dt = new Date(y, m, d);
        if (!isNaN(dt.getTime())) {
          return dt.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          });
        }
      }
      const dt = new Date(date);
      if (!isNaN(dt.getTime())) {
        return dt.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        });
      }
      return date;
    } catch {
      return date;
    }
  }, [date]);

  // Query live Google Calendar busy slots for host
  useEffect(() => {
    let isMounted = true;
    if (!workspaceId || !date || workspaceId === 'ws-primary') {
      setExternalBusySlots([]);
      return;
    }

    const parts = date.split('-').map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) return;
    const [y, m, d] = parts;

    const startOfDay = new Date(Date.UTC(y, m - 1, d, 0, 0, 0));
    const endOfDay = new Date(Date.UTC(y, m - 1, d, 23, 59, 59));

    const targetPersonId = effectiveTargetPerson.isSelf
      ? effectiveCurrentUser?.id || effectiveCurrentUser?.userId || effectiveCurrentUser?.sub || ''
      : effectiveTargetPerson.userId || effectiveTargetPerson.id || hostId;

    setIsCheckingConflicts(true);
    calendarApi
      .getHostAvailability(
        workspaceId,
        startOfDay.toISOString(),
        endOfDay.toISOString(),
        targetPersonId
      )
      .then((slots) => {
        if (isMounted) {
          setExternalBusySlots(Array.isArray(slots) ? slots : []);
          setIsCheckingConflicts(false);
        }
      })
      .catch(() => {
        if (isMounted) {
          setExternalBusySlots([]);
          setIsCheckingConflicts(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [date, workspaceId, hostId, effectiveTargetPerson, effectiveCurrentUser]);

  const computedStartTime = useMemo(() => {
    return formatTimeDisplay(timeSlot);
  }, [timeSlot]);

  const computedEndTime = useMemo(() => {
    if (!timeSlot) return '';
    const [h, m] = timeSlot.split(':').map((v) => parseInt(v, 10) || 0);
    const dt = new Date(2000, 0, 1, h, m + (Number(durationMinutes) || 45));
    return formatTimeDisplay(dt);
  }, [timeSlot, durationMinutes]);

  const computedTimeRange = useMemo(() => {
    if (!computedStartTime || !computedEndTime) return '';
    return `${computedStartTime} - ${computedEndTime}`;
  }, [computedStartTime, computedEndTime]);

  // Compute Host Slot Conflict
  const getSlotConflictInfo = useMemo(() => {
    return (slotTime, durationMins = durationMinutes, targetDateStr = date) => {
      if (!slotTime || !targetDateStr) return null;
      const [h, min] = slotTime.split(':').map(Number);
      const [y, m, d] = targetDateStr.split('-').map(Number);
      const slotStart = new Date(y, m - 1, d, h, min, 0);
      const slotEnd = new Date(slotStart.getTime() + (Number(durationMins) || 45) * 60000);

      // 1. Check Google Calendar busy events
      if (Array.isArray(externalBusySlots) && externalBusySlots.length > 0) {
        for (const busy of externalBusySlots) {
          if (!busy.start) continue;
          let bStart = new Date(busy.start);
          let bEnd = busy.end
            ? new Date(busy.end)
            : new Date(bStart.getTime() + (Number(durationMins) || 45) * 60000);
          let isOverlapping = slotStart < bEnd && slotEnd > bStart;

          if (
            !isOverlapping &&
            typeof busy.start === 'string' &&
            busy.start.includes(targetDateStr)
          ) {
            const timePart = busy.start.split('T')[1];
            if (timePart) {
              const [bH, bM] = timePart.substring(0, 5).split(':').map(Number);
              const bStartLocal = new Date(y, m - 1, d, bH, bM, 0);
              const bEndLocal = new Date(
                bStartLocal.getTime() + (Number(durationMins) || 45) * 60000
              );
              if (slotStart < bEndLocal && slotEnd > bStartLocal) {
                isOverlapping = true;
                bStart = bStartLocal;
                bEnd = bEndLocal;
              }
            }
          }

          if (isOverlapping) {
            const providerName =
              busy.provider === 'google'
                ? 'Google Calendar'
                : busy.provider === 'microsoft' || busy.provider === 'outlook'
                  ? 'Outlook Calendar'
                  : 'External Calendar';
            return {
              type: 'external_calendar',
              source: busy.provider || 'google',
              sourceLabel: providerName,
              title: busy.title || `${providerName} Personal Event`,
              timeRange: `${formatSlotTimeStr(bStart)} - ${formatSlotTimeStr(bEnd)}`,
              accountEmail: busy.accountEmail || '',
              hostName: busy.hostName || effectiveTargetPerson.name || '',
            };
          }
        }
      }

      // 2. Check existing workspace CRM meetings
      if (Array.isArray(existingMeetings) && existingMeetings.length > 0) {
        for (const mtg of existingMeetings) {
          if (!mtg.startTime || mtg.status === 'cancelled') continue;

          const mtgHostId = String(mtg.hostId || mtg.host_id || '')
            .trim()
            .toLowerCase();
          const mtgHostEmail = String(mtg.hostEmail || mtg.host_email || '')
            .trim()
            .toLowerCase();
          const mtgHostName = String(mtg.hostName || mtg.host_name || '')
            .trim()
            .toLowerCase();

          let isAssignedToTarget = false;

          if (effectiveTargetPerson.isSelf) {
            const myIds = [
              effectiveCurrentUser?.id,
              effectiveCurrentUser?.userId,
              effectiveCurrentUser?.sub,
            ]
              .filter(Boolean)
              .map((id) => String(id).trim().toLowerCase());

            const myEmails = [effectiveCurrentUser?.email]
              .filter(Boolean)
              .map((e) => String(e).trim().toLowerCase());

            const myNames = [effectiveCurrentUser?.name, effectiveCurrentUser?.username]
              .filter(Boolean)
              .map((n) => String(n).trim().toLowerCase());

            const isUnassignedMtg =
              !mtgHostId || mtgHostId === 'unassigned' || mtgHostId === 'usr-1';
            const isMyMtg =
              myIds.includes(mtgHostId) ||
              myEmails.some((em) => em && (em === mtgHostEmail || mtgHostEmail.includes(em))) ||
              myNames.some((nm) => nm && (nm === mtgHostName || mtgHostName.includes(nm)));

            isAssignedToTarget = isUnassignedMtg || isMyMtg;
          } else {
            const targetIds = [effectiveTargetPerson.id, effectiveTargetPerson.userId, hostId]
              .filter(Boolean)
              .map((id) => String(id).trim().toLowerCase());
            const targetEmail = String(effectiveTargetPerson.email || '')
              .trim()
              .toLowerCase();
            const targetName = String(
              effectiveTargetPerson.name || effectiveTargetPerson.username || ''
            )
              .trim()
              .toLowerCase();

            isAssignedToTarget =
              targetIds.includes(mtgHostId) ||
              (targetEmail &&
                (mtgHostEmail === targetEmail || mtgHostEmail.includes(targetEmail))) ||
              (targetName && (mtgHostName === targetName || mtgHostName.includes(targetName)));
          }

          if (!isAssignedToTarget) continue;

          let mStart = new Date(mtg.startTime);
          let mEnd = mtg.endTime
            ? new Date(mtg.endTime)
            : new Date(mStart.getTime() + (Number(mtg.durationMinutes) || 30) * 60000);

          let isOverlapping = slotStart < mEnd && slotEnd > mStart;

          if (
            !isOverlapping &&
            typeof mtg.startTime === 'string' &&
            mtg.startTime.includes(targetDateStr)
          ) {
            const timePart = mtg.startTime.split('T')[1];
            if (timePart) {
              const [mH, mM] = timePart.substring(0, 5).split(':').map(Number);
              const mStartLocal = new Date(y, m - 1, d, mH, mM, 0);
              const mEndLocal = new Date(
                mStartLocal.getTime() + (Number(mtg.durationMinutes) || 30) * 60000
              );
              if (slotStart < mEndLocal && slotEnd > mStartLocal) {
                isOverlapping = true;
                mStart = mStartLocal;
                mEnd = mEndLocal;
              }
            }
          }

          if (isOverlapping) {
            return {
              type: 'crm_meeting',
              source: 'crm',
              sourceLabel: 'CRM Meeting',
              title: mtg.title || `Meeting: ${mtg.guestName || 'Lead'}`,
              timeRange: `${formatSlotTimeStr(mStart)} - ${formatSlotTimeStr(mEnd)}`,
              guestName: mtg.guestName || '',
              hostName: mtg.hostName || effectiveTargetPerson.name || '',
            };
          }
        }
      }

      // 3. Check buffer conflicts if bufferBeforeMinutes or bufferAfterMinutes are configured
      const effectiveBufBefore = Number(
        effectiveTargetPerson?.bufferBeforeMinutes ??
          effectiveTargetPerson?.buffer_before_minutes ??
          bufferBeforeMinutes
      );
      const effectiveBufAfter = Number(
        effectiveTargetPerson?.bufferAfterMinutes ??
          effectiveTargetPerson?.buffer_after_minutes ??
          bufferAfterMinutes
      );
      const bufBeforeMs = (effectiveBufBefore || 0) * 60000;
      const bufAfterMs = (effectiveBufAfter || 0) * 60000;

      if (bufBeforeMs > 0 || bufAfterMs > 0) {
        // 3a. Buffer check against external calendar busy slots
        if (Array.isArray(externalBusySlots) && externalBusySlots.length > 0) {
          for (const busy of externalBusySlots) {
            if (!busy.start) continue;
            let bStart = new Date(busy.start);
            let bEnd = busy.end
              ? new Date(busy.end)
              : new Date(bStart.getTime() + (Number(durationMins) || 45) * 60000);

            if (typeof busy.start === 'string' && busy.start.includes(targetDateStr)) {
              const timePart = busy.start.split('T')[1];
              if (timePart) {
                const [bH, bM] = timePart.substring(0, 5).split(':').map(Number);
                bStart = new Date(y, m - 1, d, bH, bM, 0);
                bEnd = new Date(bStart.getTime() + (Number(durationMins) || 45) * 60000);
              }
            }

            if (Math.abs(slotStart.getTime() - bStart.getTime()) > 24 * 3600000) continue;

            const providerName =
              busy.provider === 'google'
                ? 'Google Calendar'
                : busy.provider === 'microsoft' || busy.provider === 'outlook'
                  ? 'Outlook Calendar'
                  : 'External Calendar';

            // Slot is AFTER external event: check buffer after event
            if (slotStart.getTime() >= bEnd.getTime()) {
              const gapMs = slotStart.getTime() - bEnd.getTime();
              const requiredGapMs = Math.max(bufAfterMs, bufBeforeMs);
              if (gapMs >= 0 && gapMs < requiredGapMs) {
                const neededMins = Math.round(requiredGapMs / 60000);
                const availTimeStr = formatSlotTimeStr(new Date(bEnd.getTime() + requiredGapMs));
                return {
                  type: 'external_calendar',
                  isBuffer: true,
                  bufferMinutes: neededMins,
                  bufferDirection: 'after',
                  source: busy.provider || 'google',
                  sourceLabel: providerName,
                  title: busy.title || `${providerName} Personal Event`,
                  timeRange: `${formatSlotTimeStr(bStart)} - ${formatSlotTimeStr(bEnd)}`,
                  bufferNotice: `Requires ${neededMins}m buffer after event (ends at ${formatSlotTimeStr(bEnd)}). Available from ${availTimeStr}.`,
                  accountEmail: busy.accountEmail || '',
                  hostName: busy.hostName || effectiveTargetPerson.name || '',
                };
              }
            }

            // Slot is BEFORE external event: check buffer before event
            if (slotEnd.getTime() <= bStart.getTime()) {
              const gapMs = bStart.getTime() - slotEnd.getTime();
              const requiredGapMs = Math.max(bufBeforeMs, bufAfterMs);
              if (gapMs >= 0 && gapMs < requiredGapMs) {
                const neededMins = Math.round(requiredGapMs / 60000);
                const availUntilStr = formatSlotTimeStr(new Date(bStart.getTime() - requiredGapMs));
                return {
                  type: 'external_calendar',
                  isBuffer: true,
                  bufferMinutes: neededMins,
                  bufferDirection: 'before',
                  source: busy.provider || 'google',
                  sourceLabel: providerName,
                  title: busy.title || `${providerName} Personal Event`,
                  timeRange: `${formatSlotTimeStr(bStart)} - ${formatSlotTimeStr(bEnd)}`,
                  bufferNotice: `Requires ${neededMins}m buffer before event (starts at ${formatSlotTimeStr(bStart)}). Available until ${availUntilStr}.`,
                  accountEmail: busy.accountEmail || '',
                  hostName: busy.hostName || effectiveTargetPerson.name || '',
                };
              }
            }
          }
        }

        // 3b. Buffer check against existing workspace CRM meetings
        if (Array.isArray(existingMeetings) && existingMeetings.length > 0) {
          for (const mtg of existingMeetings) {
            if (!mtg.startTime || mtg.status === 'cancelled') continue;

            const mtgHostId = String(mtg.hostId || mtg.host_id || '')
              .trim()
              .toLowerCase();
            const mtgHostEmail = String(mtg.hostEmail || mtg.host_email || '')
              .trim()
              .toLowerCase();
            const mtgHostName = String(mtg.hostName || mtg.host_name || '')
              .trim()
              .toLowerCase();

            let isAssignedToTarget = false;

            if (effectiveTargetPerson.isSelf) {
              const myIds = [
                effectiveCurrentUser?.id,
                effectiveCurrentUser?.userId,
                effectiveCurrentUser?.sub,
              ]
                .filter(Boolean)
                .map((id) => String(id).trim().toLowerCase());

              const myEmails = [effectiveCurrentUser?.email]
                .filter(Boolean)
                .map((e) => String(e).trim().toLowerCase());

              const myNames = [effectiveCurrentUser?.name, effectiveCurrentUser?.username]
                .filter(Boolean)
                .map((n) => String(n).trim().toLowerCase());

              const isUnassignedMtg =
                !mtgHostId || mtgHostId === 'unassigned' || mtgHostId === 'usr-1';
              const isMyMtg =
                myIds.includes(mtgHostId) ||
                myEmails.some((em) => em && (em === mtgHostEmail || mtgHostEmail.includes(em))) ||
                myNames.some((nm) => nm && (nm === mtgHostName || mtgHostName.includes(nm)));

              isAssignedToTarget = isUnassignedMtg || isMyMtg;
            } else {
              const targetIds = [effectiveTargetPerson.id, effectiveTargetPerson.userId, hostId]
                .filter(Boolean)
                .map((id) => String(id).trim().toLowerCase());
              const targetEmail = String(effectiveTargetPerson.email || '')
                .trim()
                .toLowerCase();
              const targetName = String(
                effectiveTargetPerson.name || effectiveTargetPerson.username || ''
              )
                .trim()
                .toLowerCase();

              isAssignedToTarget =
                targetIds.includes(mtgHostId) ||
                (targetEmail &&
                  (mtgHostEmail === targetEmail || mtgHostEmail.includes(targetEmail))) ||
                (targetName && (mtgHostName === targetName || mtgHostName.includes(targetName)));
            }

            if (!isAssignedToTarget) continue;

            let mStart = new Date(mtg.startTime);
            let mEnd = mtg.endTime
              ? new Date(mtg.endTime)
              : new Date(mStart.getTime() + (Number(mtg.durationMinutes) || 30) * 60000);

            if (typeof mtg.startTime === 'string' && mtg.startTime.includes(targetDateStr)) {
              const timePart = mtg.startTime.split('T')[1];
              if (timePart) {
                const [mH, mM] = timePart.substring(0, 5).split(':').map(Number);
                mStart = new Date(y, m - 1, d, mH, mM, 0);
                mEnd = new Date(mStart.getTime() + (Number(mtg.durationMinutes) || 30) * 60000);
              }
            }

            if (Math.abs(slotStart.getTime() - mStart.getTime()) > 24 * 3600000) continue;

            // Slot is AFTER CRM meeting: check buffer after meeting
            if (slotStart.getTime() >= mEnd.getTime()) {
              const gapMs = slotStart.getTime() - mEnd.getTime();
              const requiredGapMs = Math.max(bufAfterMs, bufBeforeMs);
              if (gapMs >= 0 && gapMs < requiredGapMs) {
                const neededMins = Math.round(requiredGapMs / 60000);
                const availTimeStr = formatSlotTimeStr(new Date(mEnd.getTime() + requiredGapMs));
                return {
                  type: 'crm_meeting',
                  isBuffer: true,
                  bufferMinutes: neededMins,
                  bufferDirection: 'after',
                  source: 'crm',
                  sourceLabel: 'CRM Meeting',
                  title: mtg.title || `Meeting: ${mtg.guestName || 'Lead'}`,
                  timeRange: `${formatSlotTimeStr(mStart)} - ${formatSlotTimeStr(mEnd)}`,
                  bufferNotice: `Requires ${neededMins}m buffer after meeting (ends at ${formatSlotTimeStr(mEnd)}). Available from ${availTimeStr}.`,
                  guestName: mtg.guestName || '',
                  hostName: mtg.hostName || effectiveTargetPerson.name || '',
                };
              }
            }

            // Slot is BEFORE CRM meeting: check buffer before meeting
            if (slotEnd.getTime() <= mStart.getTime()) {
              const gapMs = mStart.getTime() - slotEnd.getTime();
              const requiredGapMs = Math.max(bufBeforeMs, bufAfterMs);
              if (gapMs >= 0 && gapMs < requiredGapMs) {
                const neededMins = Math.round(requiredGapMs / 60000);
                const availUntilStr = formatSlotTimeStr(new Date(mStart.getTime() - requiredGapMs));
                return {
                  type: 'crm_meeting',
                  isBuffer: true,
                  bufferMinutes: neededMins,
                  bufferDirection: 'before',
                  source: 'crm',
                  sourceLabel: 'CRM Meeting',
                  title: mtg.title || `Meeting: ${mtg.guestName || 'Lead'}`,
                  timeRange: `${formatSlotTimeStr(mStart)} - ${formatSlotTimeStr(mEnd)}`,
                  bufferNotice: `Requires ${neededMins}m buffer before next meeting (starts at ${formatSlotTimeStr(mStart)}). Available until ${availUntilStr}.`,
                  guestName: mtg.guestName || '',
                  hostName: mtg.hostName || effectiveTargetPerson.name || '',
                };
              }
            }
          }
        }
      }

      return null;
    };
  }, [
    date,
    durationMinutes,
    externalBusySlots,
    existingMeetings,
    effectiveTargetPerson,
    effectiveCurrentUser,
    hostId,
    bufferBeforeMinutes,
    bufferAfterMinutes,
  ]);

  const slotsConflictMap = useMemo(() => {
    const map = {};
    dynamicTimeSlots.forEach((slot) => {
      map[slot] = getSlotConflictInfo(slot, durationMinutes);
    });
    return map;
  }, [getSlotConflictInfo, durationMinutes, dynamicTimeSlots]);

  const currentSlotConflict = useMemo(() => {
    return slotsConflictMap[timeSlot] || null;
  }, [slotsConflictMap, timeSlot]);

  // Smart multi-day slot suggestions respecting host working days & hours
  const smartMultiDaySuggestions = useMemo(() => {
    if (!currentSlotConflict || !date) return [];

    const suggestions = [];
    const durMins = Number(durationMinutes) || 30;

    const getDateLabel = (dStr) => {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, '0');
      const d = String(now.getDate()).padStart(2, '0');
      const todayString = `${y}-${m}-${d}`;

      if (dStr === todayString) return 'Today';

      const [year, month, day] = dStr.split('-').map(Number);
      const dt = new Date(year, month - 1, day);
      return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    };

    // 1. Same-day available slots (if host working day)
    if (isWorkingDay(date)) {
      const sameDaySlots = dynamicTimeSlots.filter(
        (s) => !isSlotInPast(s, date) && !slotsConflictMap[s]
      );
      sameDaySlots.slice(0, 3).forEach((s) => {
        suggestions.push({
          dateStr: date,
          dateLabel: getDateLabel(date),
          slot: s,
          timeLabel: formatTimeDisplay(s),
        });
      });
    }

    // 2. Look ahead across next 5 days for available slots
    const [currY, currM, currD] = date.split('-').map(Number);
    const baseDate = new Date(currY, currM - 1, currD);

    for (let i = 1; i <= 5 && suggestions.length < 6; i++) {
      const futureDt = new Date(baseDate);
      futureDt.setDate(baseDate.getDate() + i);
      const fy = futureDt.getFullYear();
      const fm = String(futureDt.getMonth() + 1).padStart(2, '0');
      const fd = String(futureDt.getDate()).padStart(2, '0');
      const fDateStr = `${fy}-${fm}-${fd}`;

      if (!isWorkingDay(fDateStr)) continue;

      const fSlots = generateHostTimeSlots(hostWorkingHoursStart, hostWorkingHoursEnd, durMins);
      const availableFSlots = fSlots.filter((s) => !getSlotConflictInfo(s, durMins, fDateStr));

      availableFSlots.slice(0, 2).forEach((s) => {
        if (suggestions.length < 6) {
          suggestions.push({
            dateStr: fDateStr,
            dateLabel: getDateLabel(fDateStr),
            slot: s,
            timeLabel: formatTimeDisplay(s),
          });
        }
      });
    }

    return suggestions;
  }, [
    currentSlotConflict,
    date,
    durationMinutes,
    dynamicTimeSlots,
    slotsConflictMap,
    isWorkingDay,
    hostWorkingHoursStart,
    hostWorkingHoursEnd,
    getSlotConflictInfo,
  ]);

  // Lead Time Slot Conflict Detection for all selected leads
  const leadConflicts = useMemo(() => {
    if (!Array.isArray(existingMeetings) || existingMeetings.length === 0 || !date || !timeSlot) {
      return {};
    }

    const [h, min] = timeSlot.split(':').map(Number);
    const [y, m, d] = date.split('-').map(Number);
    const slotStart = new Date(y, m - 1, d, h, min, 0);
    const slotEnd = new Date(slotStart.getTime() + (Number(durationMinutes) || 30) * 60000);

    const conflicts = {};

    availableLeads.forEach((lead) => {
      const lId = String(lead.id || lead.crmId || '')
        .trim()
        .toLowerCase();
      const lEmail = String(lead.email || '')
        .trim()
        .toLowerCase();
      const lName = String(lead.name || '')
        .trim()
        .toLowerCase();

      for (const mtg of existingMeetings) {
        if (!mtg.startTime || mtg.status === 'cancelled') continue;

        const mtgLeadId = String(mtg.leadId || mtg.lead_id || '')
          .trim()
          .toLowerCase();
        const mtgGuestEmail = String(mtg.guestEmail || mtg.guest_email || '')
          .trim()
          .toLowerCase();
        const mtgGuestName = String(mtg.guestName || mtg.guest_name || '')
          .trim()
          .toLowerCase();

        // 1. Direct ID match
        let matchLead = Boolean(
          lId &&
          mtgLeadId &&
          (lId === mtgLeadId || lId.includes(mtgLeadId) || mtgLeadId.includes(lId))
        );

        // 2. Check leadIds array
        if (!matchLead) {
          const leadIdsList = [
            ...(Array.isArray(mtg.leadIds) ? mtg.leadIds : []),
            ...(Array.isArray(mtg.lead_ids) ? mtg.lead_ids : []),
            ...(Array.isArray(mtg.preMeetingBrief?.leadIds) ? mtg.preMeetingBrief.leadIds : []),
            ...(Array.isArray(mtg.pre_meeting_brief?.leadIds) ? mtg.pre_meeting_brief.leadIds : []),
          ].map((id) => String(id).trim().toLowerCase());

          if (
            lId &&
            leadIdsList.some((id) => id && (id === lId || id.includes(lId) || lId.includes(id)))
          ) {
            matchLead = true;
          }
        }

        // 3. Check attendees list
        if (!matchLead) {
          const attendeesList = [
            ...(Array.isArray(mtg.attendees) ? mtg.attendees : []),
            ...(Array.isArray(mtg.preMeetingBrief?.attendees) ? mtg.preMeetingBrief.attendees : []),
            ...(Array.isArray(mtg.pre_meeting_brief?.attendees)
              ? mtg.pre_meeting_brief.attendees
              : []),
          ];

          for (const att of attendeesList) {
            if (!att) continue;
            if (typeof att === 'string') {
              const attStr = att.trim().toLowerCase();
              if (lEmail && attStr.includes(lEmail)) {
                matchLead = true;
                break;
              }
              if (lName && attStr.includes(lName)) {
                matchLead = true;
                break;
              }
              if (lId && attStr === lId) {
                matchLead = true;
                break;
              }
            } else if (typeof att === 'object') {
              const attId = String(att.id || att.crmId || att.leadId || att.lead_id || '')
                .trim()
                .toLowerCase();
              const attEmail = String(att.email || att.contact_email || '')
                .trim()
                .toLowerCase();
              const attName = String(att.name || att.full_name || '')
                .trim()
                .toLowerCase();

              if (lId && attId && (lId === attId || lId.includes(attId) || attId.includes(lId))) {
                matchLead = true;
                break;
              }
              if (
                lEmail &&
                attEmail &&
                (lEmail === attEmail || attEmail.includes(lEmail) || lEmail.includes(attEmail))
              ) {
                matchLead = true;
                break;
              }
              if (
                lName &&
                attName &&
                (lName === attName || attName.includes(lName) || lName.includes(attName))
              ) {
                matchLead = true;
                break;
              }
            }
          }
        }

        // 4. Check guestEmail (exact or comma-separated)
        if (!matchLead && lEmail && mtgGuestEmail) {
          if (lEmail === mtgGuestEmail || mtgGuestEmail.includes(lEmail)) {
            matchLead = true;
          }
        }

        // 5. Check guestName (exact or comma-separated)
        if (!matchLead && lName && mtgGuestName) {
          if (lName === mtgGuestName || mtgGuestName.includes(lName)) {
            matchLead = true;
          }
        }

        if (!matchLead) continue;

        let mStart = new Date(mtg.startTime);
        let mEnd = mtg.endTime
          ? new Date(mtg.endTime)
          : new Date(mStart.getTime() + (Number(mtg.durationMinutes) || 30) * 60000);

        let isOverlapping = slotStart < mEnd && slotEnd > mStart;

        if (!isOverlapping && typeof mtg.startTime === 'string' && mtg.startTime.includes(date)) {
          const timePart = mtg.startTime.split('T')[1];
          if (timePart) {
            const [mH, mM] = timePart.substring(0, 5).split(':').map(Number);
            const mStartLocal = new Date(y, m - 1, d, mH, mM, 0);
            const mEndLocal = new Date(
              mStartLocal.getTime() + (Number(mtg.durationMinutes) || 30) * 60000
            );
            if (slotStart < mEndLocal && slotEnd > mStartLocal) {
              isOverlapping = true;
              mStart = mStartLocal;
              mEnd = mEndLocal;
            }
          }
        }

        if (isOverlapping) {
          conflicts[lead.id] = {
            meetingTitle: mtg.title || 'Existing Meeting',
            timeRange: `${formatSlotTimeStr(mStart)} - ${formatSlotTimeStr(mEnd)}`,
          };
          break;
        }
      }
    });

    return conflicts;
  }, [availableLeads, existingMeetings, date, timeSlot, durationMinutes]);

  const conflictedSelectedLeads = useMemo(() => {
    return selectedLeadsObjects.filter((l) => Boolean(leadConflicts[l.id]));
  }, [selectedLeadsObjects, leadConflicts]);

  const isFormValid = useMemo(() => {
    if (!selectedCampaign || !isActiveAndApprovedCampaign(selectedCampaign)) return false;
    if (!isGoogleCalendarConnected) return false;
    if (isSlotInPast(timeSlot, date)) return false;
    if (!isWorkingDay(date)) return false;
    if (currentSlotConflict) return false;
    if (!meetingTopic.trim()) return false;
    if (selectedLeadIds.length === 0) return false;
    if (conflictedSelectedLeads.length > 0) return false;
    return Boolean(date && timeSlot);
  }, [
    selectedCampaign,
    isGoogleCalendarConnected,
    timeSlot,
    date,
    currentSlotConflict,
    isWorkingDay,
    meetingTopic,
    selectedLeadIds,
    conflictedSelectedLeads,
  ]);

  // Lead selection toggles
  const handleToggleLead = (leadId) => {
    setSelectedLeadIds((prev) =>
      prev.includes(leadId) ? prev.filter((id) => id !== leadId) : [...prev, leadId]
    );
  };

  const handleSelectAllLeads = () => {
    const unconflicted = filteredAvailableLeads
      .filter((l) => !leadConflicts[l.id])
      .map((l) => l.id);
    setSelectedLeadIds(unconflicted);
  };

  const handleClearSelectedLeads = () => {
    setSelectedLeadIds([]);
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (hostRef.current && !hostRef.current.contains(e.target)) setIsHostOpen(false);
      if (typeRef.current && !typeRef.current.contains(e.target)) setIsTypeOpen(false);
      if (timeDropdownRef.current && !timeDropdownRef.current.contains(e.target))
        setIsTimeOpen(false);
      if (campaignRef.current && !campaignRef.current.contains(e.target)) setIsCampaignOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isGoogleCalendarConnected) return;
    if (isSlotInPast(timeSlot, date)) return;
    if (!meetingTopic.trim()) return;
    if (selectedLeadIds.length === 0) return;
    if (currentSlotConflict || conflictedSelectedLeads.length > 0) return;

    const [hours, mins] = timeSlot.split(':').map((v) => parseInt(v, 10) || 0);
    const [y, m, d] = date.split('-').map((v) => parseInt(v, 10) || 0);
    const startDate = new Date(y, m - 1, d, hours, mins, 0);
    const startIso = startDate.toISOString();

    const endDate = new Date(startDate.getTime() + (Number(durationMinutes) || 30) * 60000);
    const endIso = endDate.toISOString();

    const letters = 'abcdefghijklmnopqrstuvwxyz';
    const randStr = (len) =>
      Array.from({ length: len }, () => letters[Math.floor(Math.random() * letters.length)]).join(
        ''
      );
    const confUrl =
      conferencingProvider === 'google_meet'
        ? `https://meet.google.com/${randStr(3)}-${randStr(4)}-${randStr(3)}`
        : conferencingProvider === 'teams'
          ? ''
          : '';

    const matchedCreatorHost = (hosts || []).find(
      (h) =>
        (effectiveCurrentUser?.id &&
          (String(h.id) === String(effectiveCurrentUser.id) ||
            String(h.userId) === String(effectiveCurrentUser.id))) ||
        (effectiveCurrentUser?.email &&
          h.email &&
          h.email.toLowerCase() === effectiveCurrentUser.email.toLowerCase())
    );

    const creatorName =
      matchedCreatorHost?.name ||
      effectiveCurrentUser?.name ||
      effectiveCurrentUser?.fullName ||
      effectiveCurrentUser?.displayName ||
      effectiveCurrentUser?.username ||
      'Host';

    const creatorEmail = effectiveCurrentUser?.email || matchedCreatorHost?.email || '';
    const creatorId =
      effectiveCurrentUser?.id || effectiveCurrentUser?.sub || matchedCreatorHost?.id || null;

    const isHostUnassigned = Boolean(
      selectedHost?.isUnassigned || hostId === 'unassigned' || !hostId
    );
    const finalMeetingTitle = meetingTopic.trim()
      ? `${selectedType?.title || 'Group Session'}: ${meetingTopic.trim()}`
      : `${selectedType?.title || 'Group Webinar'}: ${selectedCampaign?.name || 'Campaign Audience'}`;

    const leadNames = selectedLeadsObjects.map((l) => l.name).filter(Boolean);
    const leadCompanies = selectedLeadsObjects.map((l) => l.company).filter(Boolean);
    const leadEmails = selectedLeadsObjects.map((l) => l.email).filter(Boolean);

    const primaryGuestName =
      leadNames.length > 1
        ? `${leadNames.length} Attendees (${leadNames[0]} +${leadNames.length - 1})`
        : leadNames[0] || 'Group Attendees';

    const primaryCompany = leadCompanies.length > 0 ? leadCompanies.join(', ') : 'Campaign Cohort';
    const primaryEmail = leadEmails.length > 0 ? leadEmails.join(', ') : '';

    const newMeeting = {
      id: `meet-group-${Date.now()}`,
      title: finalMeetingTitle,
      meetingTypeId: selectedType?.id,
      meetingTypeName: selectedType?.title,
      isGroupMeeting: true,
      campaignId: selectedCampaign?.id || null,
      campaignName: selectedCampaign?.name || 'Target Campaign',
      campaignObjective: selectedCampaign?.objective || '',
      attendees: selectedLeadsObjects,
      attendeeCount: selectedLeadsObjects.length,
      leadId: selectedLeadsObjects[0]?.id || null,
      leadIds: selectedLeadsObjects.map((l) => l.id),
      isUnassigned: isHostUnassigned,
      hostId: selectedHost.isUnassigned ? creatorId : selectedHost.id,
      hostName: selectedHost.isUnassigned ? creatorName : selectedHost.name,
      hostEmail: selectedHost.isUnassigned ? creatorEmail : selectedHost.email,
      guestName: primaryGuestName,
      guestEmail: primaryEmail,
      guestCompany: primaryCompany,
      guestTitle: `${selectedLeadsObjects.length} Decision Makers`,
      guestNotes: guestNotes.trim(),
      startTime: startIso,
      endTime: endIso,
      durationMinutes: Number(durationMinutes) || 30,
      status: 'confirmed',
      cancellationReason: null,
      conferencingProvider: conferencingProvider,
      conferencingUrl: confUrl,
      leadScore: 88,
      dealEstimate: selectedCampaign?.expectedPipeline || 'RM 50,000',
      currency: 'MYR',
      preMeetingBrief: {
        isGroupMeeting: true,
        campaignId: selectedCampaign?.id,
        campaignName: selectedCampaign?.name,
        campaignObjective: selectedCampaign?.objective || '',
        objective: selectedCampaign?.objective || '',
        accountSummary: `Multi-attendee group session for ${selectedCampaign?.name || 'campaign'} targeting ${selectedLeadsObjects.length} leads.`,
        engagementHistory: `Session scheduled for ${selectedType?.title || 'group presentation'}.`,
        primaryObjectives: selectedCampaign?.objective ? [selectedCampaign.objective] : [],
        leadIds: selectedLeadsObjects.map((l) => l.id || l.crmId).filter(Boolean),
        attendees: selectedLeadsObjects.map((l) => ({
          id: l.id || l.crmId,
          crmId: l.crmId || l.id,
          name: l.name,
          email: l.email,
          company: l.company,
          jobTitle: l.jobTitle,
          stage: l.stage,
        })),
      },
    };

    // Auto-sync to CRM (Lead Pipeline) for each selected lead
    for (const lead of selectedLeadsObjects) {
      const crmId = lead.crmId || lead.id;
      if (crmId) {
        try {
          // If stage was 'New', advance to 'Contacted'
          if (lead.stage === 'New') {
            axios.patch(`/lead-pipeline/${crmId}`, { stage: 'Contacted' }).catch(() => {});
          }
          // Log activity note in pipeline
          axios
            .post(`/lead-pipeline/${crmId}/notes`, {
              content: `Scheduled group meeting "${finalMeetingTitle}" on ${formattedDisplayDate} (${computedTimeRange}) via campaign "${selectedCampaign?.name}".`,
            })
            .catch(() => {});
        } catch (_) {}
      }
    }

    onSchedule(newMeeting);
    onClose();
  };

  return (
    <div className="meetings-modal-overlay" onClick={onClose}>
      <div className="sched-clean-modal sched-group-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sched-modal-header">
          <div className="sched-modal-header-left">
            <div className="sched-modal-header-icon sched-group-header-icon">
              <i className="fas fa-users"></i>
            </div>
            <div className="sched-modal-header-info">
              <h2 className="sched-modal-title">Group & Multi-Attendee Meeting</h2>
              <p className="sched-modal-subtitle">
                Select a campaign, import target leads, and schedule a synchronized multi-attendee
                session.
              </p>
            </div>
          </div>
          <button type="button" className="sched-modal-close-btn" onClick={onClose} title="Close">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="sched-modal-form">
          <div className="sched-modal-body">
            {!isCalendarConnected && (
              <div className="sched-google-cal-warning-banner">
                <div className="sched-warning-banner-icon">
                  <i className="fas fa-calendar-alt"></i>
                </div>
                <div className="sched-warning-banner-content">
                  <div className="sched-warning-banner-title">Calendar Not Connected</div>
                  <div className="sched-warning-banner-text">
                    You must connect your Google or Microsoft Calendar before scheduling group
                    meetings to generate valid video meeting links and sync calendar availability.
                  </div>
                </div>
              </div>
            )}

            {/* Section 1: Session & Meeting Details */}
            <div className="sched-section-block">
              <div className="sched-section-header">
                <span className="sched-section-title">
                  <i
                    className="far fa-calendar-check"
                    style={{ marginRight: '6px', color: '#2563eb' }}
                  ></i>
                  Session & Meeting Details
                </span>
              </div>

              <div className="sched-grid-2">
                {/* 1. Host Dropdown */}
                <div className="sched-form-group" ref={hostRef}>
                  <label className="sched-label">Assigned Host</label>
                  <div className="sched-dropdown-wrap">
                    <button
                      type="button"
                      className={`sched-select-trigger ${isHostOpen ? 'active' : ''}`}
                      onClick={() => {
                        setIsHostOpen(!isHostOpen);
                        setIsTypeOpen(false);
                        setIsTimeOpen(false);
                        setIsCampaignOpen(false);
                      }}
                    >
                      <div className="sched-trigger-left">
                        {selectedHost?.isUnassigned ? (
                          <>
                            <span className="sched-unassigned-avatar">
                              <i className="fas fa-user-slash"></i>
                            </span>
                            <span className="sched-host-selected-name">Unassigned</span>
                          </>
                        ) : (
                          <>
                            <span
                              className="sched-host-avatar-pill"
                              style={{
                                backgroundColor: getAvatarColor(
                                  selectedHost?.name || selectedHost?.username
                                ),
                              }}
                            >
                              {getInitials(selectedHost?.name)}
                            </span>
                            <span className="sched-host-selected-name">{selectedHost?.name}</span>
                            {selectedHost?.role && (
                              <span className="sched-host-role-badge">
                                {formatRoleLabel(selectedHost.role)}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      <i
                        className={`fas fa-chevron-down sched-chevron ${isHostOpen ? 'open' : ''}`}
                      ></i>
                    </button>

                    {isHostOpen && (
                      <div className="sched-dropdown-menu">
                        <div className="sched-dropdown-options-list">
                          <div
                            className={`sched-dropdown-option-item ${
                              !hostId || hostId === 'unassigned' ? 'selected' : ''
                            }`}
                            onClick={() => {
                              setHostId('');
                              setIsHostOpen(false);
                            }}
                          >
                            <div className="sched-opt-host-left">
                              <span className="sched-unassigned-avatar">
                                <i className="fas fa-user-slash"></i>
                              </span>
                              <div className="sched-opt-host-details">
                                <div className="sched-opt-host-name-row">
                                  <span className="sched-opt-name" style={{ color: '#475569' }}>
                                    Unassigned
                                  </span>
                                </div>
                                <span className="sched-opt-email">
                                  Current account hosts session
                                </span>
                              </div>
                            </div>
                            {(!hostId || hostId === 'unassigned') && (
                              <i className="fas fa-check sched-opt-check"></i>
                            )}
                          </div>

                          {assignableHosts.map((h) => {
                            const isSelected = h.id === hostId;
                            return (
                              <div
                                key={h.id}
                                className={`sched-dropdown-option-item ${isSelected ? 'selected' : ''}`}
                                onClick={() => {
                                  setHostId(h.id);
                                  setIsHostOpen(false);
                                }}
                              >
                                <div className="sched-opt-host-left">
                                  <span
                                    className="sched-host-avatar-pill"
                                    style={{
                                      backgroundColor: getAvatarColor(h.name || h.username),
                                    }}
                                  >
                                    {getInitials(h.name || h.username)}
                                  </span>
                                  <div className="sched-opt-host-details">
                                    <div className="sched-opt-host-name-row">
                                      <span className="sched-opt-name">{h.name || h.username}</span>
                                      <span className="sched-opt-role-tag">
                                        {formatRoleLabel(h.rawRole || h.role)}
                                      </span>
                                    </div>
                                    <span className="sched-opt-email">{h.email}</span>
                                  </div>
                                </div>
                                {isSelected && <i className="fas fa-check sched-opt-check"></i>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* 2. Meeting Type Dropdown */}
                <div className="sched-form-group" ref={typeRef}>
                  <label className="sched-label">Meeting Type</label>
                  <div className="sched-dropdown-wrap">
                    <button
                      type="button"
                      className={`sched-select-trigger ${isTypeOpen ? 'active' : ''}`}
                      onClick={() => {
                        setIsTypeOpen(!isTypeOpen);
                        setIsHostOpen(false);
                        setIsTimeOpen(false);
                        setIsCampaignOpen(false);
                      }}
                    >
                      <div className="sched-trigger-left">
                        <span className="sched-type-selected-title">{selectedType?.title}</span>
                      </div>
                      <i
                        className={`fas fa-chevron-down sched-chevron ${isTypeOpen ? 'open' : ''}`}
                      ></i>
                    </button>

                    {isTypeOpen && (
                      <div className="sched-dropdown-menu">
                        <div className="sched-dropdown-options-list">
                          {meetingTypes.map((t) => {
                            const isSelected = t.id === meetingTypeId;
                            return (
                              <div
                                key={t.id}
                                className={`sched-dropdown-option-item ${isSelected ? 'selected' : ''}`}
                                onClick={() => {
                                  setMeetingTypeId(t.id);
                                  setIsTypeOpen(false);
                                }}
                              >
                                <div className="sched-opt-type-left">
                                  <div className="sched-opt-type-details">
                                    <span className="sched-opt-type-title">{t.title}</span>
                                    {t.description && (
                                      <p className="sched-opt-type-desc">{t.description}</p>
                                    )}
                                  </div>
                                </div>
                                {isSelected && <i className="fas fa-check sched-opt-check"></i>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Meeting Title / Topic */}
              <div className="sched-form-group">
                <label className="sched-label">
                  Meeting Title / Topic <span className="sched-req">*</span>
                </label>
                <div className="sched-title-input-wrap">
                  <span className="sched-title-prefix">
                    {selectedType?.title || 'Group Session'}:
                  </span>
                  <input
                    type="text"
                    className="sched-title-input-field"
                    placeholder="Enter meeting topic (e.g. SEA Q4 Strategy & Live Demo)"
                    value={meetingTopic}
                    onChange={(e) => {
                      setHasUserEditedTopic(true);
                      setMeetingTopic(e.target.value);
                    }}
                    required
                  />
                </div>
              </div>

              {/* Duration Pills */}
              <div className="sched-form-group">
                <div className="sched-label-row">
                  <label className="sched-label">
                    Duration <span className="sched-req">*</span>
                  </label>
                  <span className="sched-duration-selected-tag">{durationMinutes} min</span>
                </div>
                <div className="sched-duration-pills">
                  {DURATION_OPTIONS.map((dur) => (
                    <button
                      key={dur}
                      type="button"
                      className={`sched-dur-btn ${durationMinutes === dur ? 'active' : ''}`}
                      onClick={() => setDurationMinutes(dur)}
                    >
                      {dur} min
                    </button>
                  ))}
                </div>
              </div>

              {/* Date & Time Grid */}
              <div className="sched-grid-2">
                <div className="sched-form-group">
                  <label className="sched-label">
                    Date <span className="sched-req">*</span>
                  </label>
                  <input
                    type="date"
                    className={`sched-input ${!isWorkingDay(date) ? 'sched-input-warn' : ''}`}
                    value={date}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={(e) => setDate(e.target.value)}
                    required
                  />
                </div>

                <div className="sched-form-group" ref={timeDropdownRef}>
                  <label className="sched-label">
                    Time Slot <span className="sched-req">*</span>
                  </label>

                  <div className="sched-dropdown-wrap">
                    <button
                      type="button"
                      className={`sched-select-trigger ${isTimeOpen ? 'active' : ''} ${
                        currentSlotConflict ? 'has-conflict' : ''
                      }`}
                      onClick={() => {
                        setIsTimeOpen(!isTimeOpen);
                        setIsHostOpen(false);
                        setIsTypeOpen(false);
                        setIsCampaignOpen(false);
                      }}
                    >
                      <div className="sched-trigger-left">
                        <i
                          className={`far fa-clock ${currentSlotConflict ? 'text-danger' : 'text-muted'}`}
                        ></i>
                        <span>{timeSlot}</span>
                        {currentSlotConflict && (
                          <span
                            className="sched-conflict-source-tag"
                            style={{ marginLeft: '6px', fontSize: '10px' }}
                          >
                            {currentSlotConflict.isBuffer ? 'Buffer' : 'Conflict'}
                          </span>
                        )}
                      </div>
                      <i
                        className={`fas fa-chevron-down sched-chevron ${isTimeOpen ? 'open' : ''}`}
                      ></i>
                    </button>

                    {isTimeOpen && (
                      <div className="sched-dropdown-menu time-menu">
                        <div className="sched-time-menu-header">
                          <span className="sched-time-header-hours">
                            <i className="far fa-clock"></i> Host Hours:{' '}
                            {formatTimeDisplay(hostWorkingHoursStart)} -{' '}
                            {formatTimeDisplay(hostWorkingHoursEnd)}
                          </span>
                          {(bufferBeforeMinutes > 0 || bufferAfterMinutes > 0) && (
                            <span
                              className="sched-time-header-buffer"
                              style={{
                                fontSize: '11px',
                                color: '#64748b',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              <i
                                className="fas fa-hourglass-half"
                                style={{ fontSize: '10px', color: '#f59e0b' }}
                              ></i>
                              Buffers:{' '}
                              {bufferBeforeMinutes > 0 ? `${bufferBeforeMinutes}m before` : ''}
                              {bufferBeforeMinutes > 0 && bufferAfterMinutes > 0 ? ' • ' : ''}
                              {bufferAfterMinutes > 0 ? `${bufferAfterMinutes}m after` : ''}
                            </span>
                          )}
                        </div>
                        <div className="sched-time-slots-grid">
                          {dynamicTimeSlots.map((slot) => {
                            const isSelected = slot === timeSlot;
                            const isPast = isSlotInPast(slot, date);
                            const slotConflict = slotsConflictMap[slot];
                            const isConflicted = !isPast && Boolean(slotConflict);

                            let chipTitle = `Select ${slot}`;
                            if (isPast) {
                              chipTitle = 'This time slot has already passed';
                            } else if (isConflicted) {
                              if (slotConflict.isBuffer) {
                                chipTitle = `Buffer Conflict: ${slotConflict.bufferNotice || `Requires ${slotConflict.bufferMinutes}m buffer time.`} (${slotConflict.title})`;
                              } else {
                                chipTitle = `Conflict: ${slotConflict.sourceLabel} - ${slotConflict.title} (${slotConflict.timeRange})`;
                              }
                            }

                            return (
                              <button
                                key={slot}
                                type="button"
                                disabled={isPast}
                                className={`sched-time-chip ${isSelected ? 'active' : ''} ${isPast ? 'disabled' : ''} ${isConflicted ? 'conflicted' : ''}`}
                                title={chipTitle}
                                onClick={() => {
                                  if (isPast) return;
                                  setTimeSlot(slot);
                                  setIsTimeOpen(false);
                                }}
                              >
                                {slot}
                              </button>
                            );
                          })}
                        </div>
                        {dynamicTimeSlots.every((s) => isSlotInPast(s, date)) && (
                          <div
                            className="sched-no-slots-hint"
                            style={{
                              padding: '8px 10px',
                              fontSize: '11.5px',
                              color: '#ef4444',
                              textAlign: 'center',
                            }}
                          >
                            All time slots for today have passed. Please select a future date.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Real-time Conflict Warning & Availability Indicator */}
              {isCheckingConflicts ? (
                <div className="sched-conflict-status-bar checking">
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>
                    Checking live calendar conflicts for{' '}
                    {effectiveTargetPerson.isSelf ? 'current account' : effectiveTargetPerson.name}
                    ...
                  </span>
                </div>
              ) : currentSlotConflict ? (
                <div
                  className={`sched-conflict-alert-box ${currentSlotConflict.isBuffer ? 'buffer-conflict' : ''}`}
                >
                  <div className="sched-conflict-alert-main">
                    <div className="sched-conflict-alert-icon">
                      <i
                        className={
                          currentSlotConflict.isBuffer
                            ? 'fas fa-hourglass-half'
                            : 'fas fa-calendar-times'
                        }
                      ></i>
                    </div>
                    <div className="sched-conflict-alert-text">
                      <div className="sched-conflict-alert-title-row">
                        <span className="sched-conflict-alert-title">
                          {currentSlotConflict.isBuffer
                            ? 'Scheduling Buffer Conflict'
                            : 'Schedule Conflict Detected'}
                        </span>
                        <span className="sched-conflict-source-tag">
                          {currentSlotConflict.isBuffer
                            ? `${currentSlotConflict.bufferMinutes}m Buffer`
                            : currentSlotConflict.sourceLabel}{' '}
                          •{' '}
                          {effectiveTargetPerson.isSelf
                            ? 'Current Account'
                            : effectiveTargetPerson.name}
                        </span>
                      </div>
                      <p className="sched-conflict-alert-msg">
                        {currentSlotConflict.isBuffer ? (
                          <>
                            {currentSlotConflict.bufferNotice ||
                              `Requires ${currentSlotConflict.bufferMinutes}m buffer before/after meeting.`}{' '}
                            <span className="sched-conflict-time-muted">
                              (Adjacent to "{currentSlotConflict.title}" at{' '}
                              {currentSlotConflict.timeRange})
                            </span>
                          </>
                        ) : (
                          <>
                            Conflicts with <strong>"{currentSlotConflict.title}"</strong>{' '}
                            <span className="sched-conflict-time-muted">
                              ({currentSlotConflict.timeRange || computedTimeRange})
                            </span>
                          </>
                        )}
                      </p>
                    </div>
                  </div>

                  {smartMultiDaySuggestions.length > 0 && (
                    <div className="sched-conflict-suggestions-pane">
                      <div className="sched-conflict-sugg-header">
                        <span className="sched-conflict-sugg-title">
                          Recommended Available Slots ({durationMinutes}m)
                        </span>
                        <span className="sched-conflict-sugg-hint">Click a slot to switch</span>
                      </div>
                      <div className="sched-conflict-sugg-days-list">
                        {Object.entries(
                          smartMultiDaySuggestions.reduce((acc, item) => {
                            acc[item.dateLabel] = acc[item.dateLabel] || [];
                            acc[item.dateLabel].push(item);
                            return acc;
                          }, {})
                        ).map(([dateLabel, slots]) => (
                          <div key={dateLabel} className="sched-sugg-day-group">
                            <span className="sched-sugg-day-label">{dateLabel}</span>
                            <div className="sched-sugg-day-chips">
                              {slots.map((item) => (
                                <button
                                  key={`${item.dateStr}-${item.slot}`}
                                  type="button"
                                  className="sched-sugg-slot-chip"
                                  onClick={() => {
                                    setDate(item.dateStr);
                                    setTimeSlot(item.slot);
                                  }}
                                  title={`Switch to ${item.dateLabel} at ${item.timeLabel}`}
                                >
                                  {item.timeLabel}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : date && timeSlot && !isSlotInPast(timeSlot, date) ? (
                !isWorkingDay(date) ? (
                  <div className="sched-conflict-status-bar non-working">
                    <i className="fas fa-exclamation-triangle"></i>
                    <span>
                      <strong>Non-Working Day</strong> ({computedTimeRange}) — Selected date is
                      outside active working days (
                      {hostWorkingDays.map((d) => d.slice(0, 3)).join(', ')})
                    </span>
                  </div>
                ) : (
                  <div className="sched-conflict-status-bar available">
                    <i className="fas fa-check-circle"></i>
                    <span>
                      Available slot ({computedTimeRange}) — No conflicts detected for{' '}
                      {effectiveTargetPerson.isSelf
                        ? 'current account'
                        : effectiveTargetPerson.name}
                    </span>
                  </div>
                )
              ) : null}

              {/* Video Conferencing */}
              <div className="sched-form-group">
                <label className="sched-label">Video Conferencing</label>
                <div className="sched-platform-segmented">
                  <button
                    type="button"
                    className={`sched-plat-btn ${conferencingProvider === 'google_meet' ? 'active' : ''} ${
                      connectedCalendarProvider && connectedCalendarProvider !== 'google'
                        ? 'sched-plat-btn-disabled'
                        : ''
                    }`}
                    onClick={() => {
                      if (connectedCalendarProvider === 'microsoft') {
                        setPlatformNotice(
                          'Google Meet requires a connected Google Calendar. Currently connected to Microsoft.'
                        );
                        return;
                      }
                      setConferencingProvider('google_meet');
                      setPlatformNotice(null);
                    }}
                    title={
                      connectedCalendarProvider === 'microsoft'
                        ? 'Google Meet requires Google Calendar connection'
                        : 'Google Meet'
                    }
                  >
                    <i className="fab fa-google text-google" />
                    <span>Google Meet</span>
                  </button>

                  <button
                    type="button"
                    className={`sched-plat-btn ${conferencingProvider === 'teams' ? 'active' : ''} ${
                      connectedCalendarProvider !== 'microsoft' ? 'sched-plat-btn-disabled' : ''
                    }`}
                    onClick={() => {
                      if (connectedCalendarProvider !== 'microsoft') {
                        setPlatformNotice(
                          'MS Teams integration requires a connected Microsoft Calendar. Please connect in Availability & Rules.'
                        );
                        return;
                      }
                      setConferencingProvider('teams');
                      setPlatformNotice(null);
                    }}
                    title={
                      connectedCalendarProvider === 'microsoft'
                        ? 'Microsoft Teams'
                        : 'Connect Microsoft Calendar to use MS Teams'
                    }
                  >
                    <i className="fab fa-microsoft text-msteams" />
                    <span>MS Teams</span>
                  </button>
                </div>
                {platformNotice && (
                  <div className="sched-platform-dev-notice">
                    <i className="fas fa-info-circle"></i>
                    <span>{platformNotice}</span>
                  </div>
                )}
              </div>

              {/* Intake Notes */}
              <div className="sched-form-group">
                <label className="sched-label">Session Agenda / Notes</label>
                <textarea
                  className="sched-textarea"
                  placeholder="Add cohort session briefing notes, discussion topics, or preparation materials..."
                  value={guestNotes}
                  onChange={(e) => setGuestNotes(e.target.value)}
                  rows={2}
                />
              </div>
            </div>

            {/* Section 2: Campaign & Target Audience */}
            <div className="sched-section-block">
              <div className="sched-section-header">
                <span className="sched-section-title">
                  <i
                    className="fas fa-bullhorn"
                    style={{ marginRight: '6px', color: '#6366f1' }}
                  ></i>
                  Target Campaign & Audience
                </span>
                <span className="sched-group-counter-pill">
                  {selectedLeadIds.length} of {availableLeads.length} Selected{' '}
                  <span
                    style={{
                      color: selectedLeadIds.length === 0 ? '#ea580c' : '#64748b',
                      fontWeight: 500,
                    }}
                  >
                    (at least 1 lead)
                  </span>
                </span>
              </div>

              {/* 1. Target Campaign Picker Dropdown */}
              <div
                className="sched-form-group"
                ref={campaignRef}
                style={{ marginBottom: selectedCampaign ? '8px' : '14px' }}
              >
                <label className="sched-label">
                  Campaign <span className="sched-req">*</span>
                </label>
                <div className="sched-dropdown-wrap">
                  <button
                    type="button"
                    className={`sched-select-trigger ${isCampaignOpen ? 'active' : ''}`}
                    onClick={() => {
                      setIsCampaignOpen(!isCampaignOpen);
                      setIsHostOpen(false);
                      setIsTypeOpen(false);
                      setIsTimeOpen(false);
                    }}
                  >
                    <div className="sched-trigger-left">
                      <span className="sched-campaign-badge-tag">
                        <i className="fas fa-bullhorn"></i>
                      </span>
                      <span className="sched-host-selected-name" style={{ fontWeight: 600 }}>
                        {selectedCampaign?.name || 'Select a campaign'}
                      </span>
                      {selectedCampaign && (
                        <span className="sched-campaign-status-badge">
                          {selectedCampaign.targetMarket ||
                            selectedCampaign.industrySector ||
                            'Active'}
                        </span>
                      )}
                    </div>
                    <i
                      className={`fas fa-chevron-down sched-chevron ${isCampaignOpen ? 'open' : ''}`}
                    ></i>
                  </button>

                  {isCampaignOpen && (
                    <div className="sched-dropdown-menu">
                      <div className="sched-dropdown-options-list">
                        {campaignsList.length === 0 ? (
                          <div className="sched-group-leads-empty" style={{ padding: '16px 12px' }}>
                            <i
                              className="fas fa-exclamation-circle"
                              style={{ color: '#f59e0b', marginRight: '6px' }}
                            ></i>
                            <span>
                              No active & approved campaigns available. Please launch and approve a
                              campaign first.
                            </span>
                          </div>
                        ) : (
                          campaignsList.map((cmp) => {
                            const isSelected = cmp.id === selectedCampaignId;
                            return (
                              <div
                                key={cmp.id}
                                className={`sched-dropdown-option-item ${isSelected ? 'selected' : ''}`}
                                onClick={() => {
                                  setSelectedCampaignId(cmp.id);
                                  setIsCampaignOpen(false);
                                }}
                              >
                                <div className="sched-opt-host-left">
                                  <span className="sched-campaign-opt-icon">
                                    <i className="fas fa-bullhorn"></i>
                                  </span>
                                  <div className="sched-opt-host-details">
                                    <div className="sched-opt-host-name-row">
                                      <strong className="sched-opt-name">{cmp.name}</strong>
                                    </div>
                                    <span className="sched-opt-email">
                                      {cmp.targetMarket || 'Target Market'} ·{' '}
                                      {cmp.industrySector || cmp.campaignType || 'B2B'}
                                    </span>
                                  </div>
                                </div>
                                {isSelected && <i className="fas fa-check sched-opt-check"></i>}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Compact Strategy Context Pills (clean & modern) */}
              {selectedCampaign && (
                <div className="sched-campaign-context-card">
                  <div className="sched-cmp-ctx-pills-row">
                    <span className="sched-cmp-ctx-pill" title={selectedCampaign.objective || ''}>
                      <i className="fas fa-bullseye" style={{ color: '#6366f1' }}></i>
                      <strong>Goal:</strong>{' '}
                      {selectedCampaign.objective
                        ? selectedCampaign.objective.length > 70
                          ? selectedCampaign.objective.slice(0, 70) + '…'
                          : selectedCampaign.objective
                        : 'Accelerate pipeline'}
                    </span>
                    <span
                      className="sched-cmp-ctx-pill"
                      title={
                        Array.isArray(selectedCampaign.targetPersonas)
                          ? selectedCampaign.targetPersonas.join(', ')
                          : selectedCampaign.targetPersonas || ''
                      }
                    >
                      <i className="fas fa-users" style={{ color: '#8b5cf6' }}></i>
                      <strong>Personas:</strong>{' '}
                      {Array.isArray(selectedCampaign.targetPersonas) &&
                      selectedCampaign.targetPersonas.length > 0
                        ? selectedCampaign.targetPersonas.slice(0, 2).join(', ') +
                          (selectedCampaign.targetPersonas.length > 2
                            ? ` (+${selectedCampaign.targetPersonas.length - 2})`
                            : '')
                        : selectedCampaign.targetPersonas || 'Key Decision Makers'}
                    </span>
                  </div>
                </div>
              )}

              {/* 2. Select Leads List */}
              <div className="sched-form-group" style={{ marginBottom: 0, marginTop: '14px' }}>
                <label className="sched-label" style={{ marginBottom: '6px' }}>
                  Select Leads from Campaign <span className="sched-req">*</span>
                </label>
                <div className="sched-group-leads-container">
                  <div className="sched-group-leads-header">
                    <div className="sched-lead-search-input-wrap" style={{ flex: 1 }}>
                      <i className="fas fa-search sched-lead-search-icon" />
                      <input
                        type="text"
                        placeholder="Filter campaign leads by name, company, job title, email..."
                        value={leadFilterQuery}
                        onChange={(e) => setLeadFilterQuery(e.target.value)}
                        className="sched-lead-search-input"
                      />
                    </div>
                    <div className="sched-group-lead-actions">
                      <button
                        type="button"
                        className="sched-group-action-btn"
                        onClick={handleSelectAllLeads}
                      >
                        <i className="fas fa-check-double"></i> Select All
                      </button>
                      <button
                        type="button"
                        className="sched-group-action-btn"
                        onClick={handleClearSelectedLeads}
                      >
                        <i className="fas fa-times"></i> Clear
                      </button>
                    </div>
                  </div>

                  {isLoadingLeads ? (
                    <div className="sched-group-leads-loading">
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>Loading campaign target leads...</span>
                    </div>
                  ) : availableLeads.length === 0 ? (
                    <div
                      className="sched-group-leads-empty"
                      style={{
                        padding: '24px 16px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '6px',
                        textAlign: 'center',
                      }}
                    >
                      <i
                        className="fas fa-users-slash"
                        style={{ fontSize: '22px', color: '#94a3b8', marginBottom: '4px' }}
                      ></i>
                      <strong style={{ color: '#334155', fontSize: '13px' }}>
                        No leads found in this campaign.
                      </strong>
                      <span style={{ fontSize: '12px', color: '#64748b' }}>
                        This campaign does not have any imported or enrolled leads yet.
                      </span>
                    </div>
                  ) : filteredAvailableLeads.length === 0 ? (
                    <div className="sched-group-leads-empty">
                      <i className="fas fa-user-friends"></i>
                      <span>No leads matching "{leadFilterQuery}"</span>
                    </div>
                  ) : (
                    <div className="sched-group-leads-list">
                      {filteredAvailableLeads.map((lead) => {
                        const isSelected = selectedLeadIds.includes(lead.id);
                        const hasConflict = Boolean(leadConflicts[lead.id]);
                        const conflictInfo = leadConflicts[lead.id];

                        if (hasConflict) {
                          return (
                            <div
                              key={lead.id}
                              className="sched-group-lead-item conflicted"
                              title={`Unavailable: already has a meeting (${conflictInfo.timeRange})`}
                            >
                              <div className="sched-group-lead-checkbox-wrap">
                                <input
                                  type="checkbox"
                                  checked={false}
                                  disabled={true}
                                  className="sched-group-lead-chk"
                                  readOnly
                                />
                              </div>

                              <span
                                className="sched-lead-avatar sched-lead-avatar-muted"
                                style={{ backgroundColor: getAvatarColor(lead.name) }}
                              >
                                {getInitials(lead.name)}
                              </span>

                              <div className="sched-group-lead-info">
                                <div className="sched-group-lead-name-row">
                                  <strong className="sched-group-lead-name">{lead.name}</strong>
                                  <span className="sched-group-lead-title">{lead.jobTitle}</span>
                                  {lead.stage && (
                                    <span
                                      className={`sched-lead-stage-badge stage-${String(
                                        lead.stage || 'new'
                                      )
                                        .toLowerCase()
                                        .replace(/\s+/g, '-')}`}
                                    >
                                      {lead.stage}
                                    </span>
                                  )}
                                  <span
                                    className="sched-lead-busy-pill"
                                    style={{ marginLeft: 'auto' }}
                                  >
                                    <i className="far fa-calendar-times"></i> Busy
                                  </span>
                                </div>
                                <div className="sched-group-lead-meta-row">
                                  {lead.company && (
                                    <span className="sched-lead-meta-item">
                                      <i className="fas fa-building" /> {lead.company}
                                    </span>
                                  )}
                                  {lead.email && (
                                    <span className="sched-lead-meta-item">
                                      <i className="fas fa-envelope" /> {lead.email}
                                    </span>
                                  )}
                                  {lead.dealEstimate && (
                                    <span className="sched-lead-meta-item sched-group-deal-est">
                                      <i className="fas fa-tag" /> {lead.dealEstimate}
                                    </span>
                                  )}
                                </div>
                                <div className="sched-lead-conflict-reason-row">
                                  <i className="fas fa-ban text-danger"></i>
                                  <span className="sched-lead-conflict-reason-text">
                                    Already has meeting ({conflictInfo.timeRange})
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div
                            key={lead.id}
                            className={`sched-group-lead-item ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleToggleLead(lead.id)}
                          >
                            <div className="sched-group-lead-checkbox-wrap">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleLead(lead.id)}
                                onClick={(e) => e.stopPropagation()}
                                className="sched-group-lead-chk"
                              />
                            </div>

                            <span
                              className="sched-lead-avatar"
                              style={{ backgroundColor: getAvatarColor(lead.name) }}
                            >
                              {getInitials(lead.name)}
                            </span>

                            <div className="sched-group-lead-info">
                              <div className="sched-group-lead-name-row">
                                <strong className="sched-group-lead-name">{lead.name}</strong>
                                <span className="sched-group-lead-title">{lead.jobTitle}</span>
                                {lead.stage && (
                                  <span
                                    className={`sched-lead-stage-badge stage-${String(
                                      lead.stage || 'new'
                                    )
                                      .toLowerCase()
                                      .replace(/\s+/g, '-')}`}
                                  >
                                    {lead.stage}
                                  </span>
                                )}
                              </div>
                              <div className="sched-group-lead-meta-row">
                                <span className="sched-lead-meta-item">
                                  <i className="fas fa-building" /> {lead.company}
                                </span>
                                <span className="sched-lead-meta-item">
                                  <i className="fas fa-envelope" /> {lead.email}
                                </span>
                                {lead.dealEstimate && (
                                  <span className="sched-lead-meta-item sched-group-deal-est">
                                    <i className="fas fa-tag" /> {lead.dealEstimate}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {conflictedSelectedLeads.length > 0 && (
                    <div
                      className="sched-lead-selected-conflict-warning"
                      style={{ marginTop: '8px' }}
                    >
                      <i className="fas fa-exclamation-triangle"></i>
                      <span>
                        <strong>Lead Conflict Detected:</strong>{' '}
                        {conflictedSelectedLeads.map((l) => l.name).join(', ')} already has another
                        meeting booked at this slot. Please deselect them or select an alternate
                        time slot.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Simple Clean Footer */}
          <div className="sched-modal-footer">
            <div className="sched-footer-summary">
              <i className="far fa-calendar-check sched-summary-icon" />
              <span>
                <strong>{formattedDisplayDate || date}</strong> · {computedTimeRange} (
                {durationMinutes}m) ·{' '}
                {conferencingProvider === 'google_meet'
                  ? 'Google Meet'
                  : conferencingProvider === 'teams'
                    ? 'MS Teams'
                    : 'Zoom'}
              </span>
            </div>

            <div className="sched-footer-actions">
              <button type="button" className="sched-btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className={`sched-btn-primary ${currentSlotConflict || conflictedSelectedLeads.length > 0 ? 'has-conflict-lock' : ''} ${!isGoogleCalendarConnected ? 'has-cal-lock' : ''}`}
                disabled={!isFormValid}
                title={
                  !isGoogleCalendarConnected
                    ? 'Please connect your Google or Microsoft Calendar in Availability & Rules before scheduling.'
                    : currentSlotConflict
                      ? currentSlotConflict.isBuffer
                        ? `Buffer conflict: Host requires ${currentSlotConflict.bufferMinutes}m buffer time.`
                        : 'Schedule conflict detected. Please select an available slot above.'
                      : conflictedSelectedLeads.length > 0
                        ? 'Schedule conflict detected. Please select an available slot above.'
                        : ''
                }
              >
                {!isGoogleCalendarConnected
                  ? 'Connect Calendar to Schedule'
                  : currentSlotConflict
                    ? currentSlotConflict.isBuffer
                      ? 'Buffer Conflict'
                      : 'Conflict Detected'
                    : conflictedSelectedLeads.length > 0
                      ? 'Conflict Detected'
                      : 'Schedule Meeting'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
